import { Colors } from "@stacc/flow-ui-components";
import type { StatisticaPostScoreVariables, TinkIncomeReport, TinkTransaction } from "../../../types/tink";
import { InfoSectionWrapper } from "../styles";
import {
  InfoSectionLessThanThreeWrapper,
  InfoSectionAccountWrapper,
  InfoSectionAccountInfoWrapper,
  InfoSectionAccountInfoSection,
  InfoSectionAccountInfoBold,
  InfoSectionTransactionInfoWrapper,
  InfoSectionTransactionInfoDate,
  InfoSectionTransactionDetails,
  InfoSectionTransactionDetailsPadding,
} from "../manual-payout-approval/styles";
import {
  IncomeCalculationWrapper,
  IncomeHeader,
  IncomeRow,
  IncomeValue,
} from "./style";



interface TinkTransactionsViewProps {
  flow: {
    caseData?: {
      data: {
        agentApiApplication?: {
          applicant?: {
            psd2Data?: string;
          };
        };
        integrations?: {
          tink?: {
            applicant?: TinkIncomeReport;
            coapplicant?: TinkIncomeReport;
          };
          statistica?: {
            SE_PostScore?: {
              variables?: StatisticaPostScoreVariables;
            };
          };
        };
      };
    };
  };
  accountId?: string;
  applicantType?: "applicant" | "coapplicant";
}

const parseAgentPsd2Data = (psd2DataString: string | undefined): TinkIncomeReport | null => {
  if (!psd2DataString) return null;
  try {
    return JSON.parse(psd2DataString) as TinkIncomeReport;
  } catch {
    return null;
  }
};

const formatCurrency = (value: number | undefined): string => {
  if (value === undefined || value === null) return "-";
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: "SEK",
    maximumFractionDigits: 0,
  }).format(value);
};

const IncomeCalculationSection = ({
  variables,
  applicantType,
}: {
  variables: StatisticaPostScoreVariables | undefined;
  applicantType: "applicant" | "coapplicant";
}) => {
  if (!variables) return null;

  const prefix = applicantType === "coapplicant" ? "sumCoIncome" : "sumMainIncome";
  const tinkIncomeKey = applicantType === "coapplicant" ? "tinkIncomeCoApp" : "tinkIncome";

  const monthLabels = [
    "Current month",
    "1 month ago",
    "2 months ago",
    "3 months ago",
    "4 months ago",
    "5 months ago",
    "6 months ago",
  ];

  const incomeFields = [0, 1, 2, 3, 4, 5, 6].map((i) => ({
    label: monthLabels[i],
    value: variables[`${prefix}_${i}`],
  }));

  const hasAnyValue = incomeFields.some((field) => field.value !== undefined && field.value !== 0) ||
    (variables[tinkIncomeKey] !== undefined && variables[tinkIncomeKey] !== 0);

  if (!hasAnyValue) return null;

  return (
    <IncomeCalculationWrapper>
      <IncomeHeader>Inkomstberegning (Statistica)</IncomeHeader>
      {incomeFields.map((field) => (
        <IncomeRow key={field.label}>
          <span>{field.label}</span>
          <IncomeValue>{formatCurrency(field.value)}</IncomeValue>
        </IncomeRow>
      ))}
      {variables[tinkIncomeKey] !== undefined && (
        <IncomeRow $highlighted>
          <span style={{ fontWeight: 600 }}>{tinkIncomeKey}</span>
          <IncomeValue style={{ fontWeight: 600 }}>{formatCurrency(variables[tinkIncomeKey])}</IncomeValue>
        </IncomeRow>
      )}
    </IncomeCalculationWrapper>
  );
};

const dateFormatOption: Intl.DateTimeFormatOptions = {
  dateStyle: "full",
  timeStyle: "short",
  hour12: false,
};

const formatDate = (time: string | undefined): string => {
  if (!time) return "-";
  const date = new Date(time);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("sv-SE", dateFormatOption).format(date);
};

const formatAmountWithColor = (amount: TinkTransaction["amount"]) => {
  if (!amount?.value) return <span>-</span>;
  const value = amount.value.unscaledValue / (10 ** (amount.value.scale || 0));
  const formatted = new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: amount.currencyCode || "SEK",
  }).format(value);

  if (value < 0) {
    return <div style={{ color: Colors.Ferrari, width: "max-content" }}>{formatted}</div>;
  }
  return <div style={{ color: Colors.Grass }}>{formatted}</div>;
};

export const TinkTransactionsView = ({ flow, accountId, applicantType = "applicant" }: TinkTransactionsViewProps) => {
  const data = flow?.caseData?.data;

  // Get postscore Tink data
  const postscoreTinkData = applicantType === "coapplicant"
    ? data?.integrations?.tink?.coapplicant
    : data?.integrations?.tink?.applicant;

  // Get agent-submitted Tink data (only for applicant, as agents don't submit coapplicant data)
  const agentTinkData = applicantType === "applicant"
    ? parseAgentPsd2Data(data?.agentApiApplication?.applicant?.psd2Data)
    : null;

  // Use postscore data if available, otherwise use agent data
  const tinkData = postscoreTinkData || agentTinkData;
  const postScoreVariables = data?.integrations?.statistica?.SE_PostScore?.variables;

  const account = tinkData?.accounts?.find(acc => acc.id === accountId);
  const streams = tinkData?.income?.streams?.filter(stream => stream.accountId === accountId) || [];

  const allTransactions = streams.flatMap(stream => stream.transactions || []);
  const getTimestamp = (time: string | undefined): number => {
    if (!time) return 0;
    const parsed = Date.parse(time);
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  const sortedTransactions = [...allTransactions].sort(
    (a, b) => getTimestamp(b.time) - getTimestamp(a.time)
  );

  if (!accountId || sortedTransactions.length === 0) {
    return (
      <InfoSectionWrapper>
        <InfoSectionLessThanThreeWrapper>
          <p>No transactions available for this account</p>
          <IncomeCalculationSection variables={postScoreVariables} applicantType={applicantType} />
        </InfoSectionLessThanThreeWrapper>
      </InfoSectionWrapper>
    );
  }

  return (
    <InfoSectionWrapper>
      <InfoSectionLessThanThreeWrapper>
        <InfoSectionAccountWrapper>
          <InfoSectionAccountInfoWrapper>
            <InfoSectionAccountInfoSection>
              {account?.financialInstitutionName || "Account"}
              <InfoSectionAccountInfoBold>{account?.accountNumber || accountId}</InfoSectionAccountInfoBold>
            </InfoSectionAccountInfoSection>
            <InfoSectionAccountInfoSection style={{ alignItems: "flex-end" }}>
              Transactions
              <InfoSectionAccountInfoBold>{sortedTransactions.length}</InfoSectionAccountInfoBold>
            </InfoSectionAccountInfoSection>
          </InfoSectionAccountInfoWrapper>
          {sortedTransactions.map((tx) => (
            <InfoSectionTransactionInfoWrapper key={tx.id}>
              <InfoSectionTransactionInfoDate>
                {formatDate(tx.time)}
              </InfoSectionTransactionInfoDate>
              <InfoSectionTransactionDetails>
                <InfoSectionTransactionDetailsPadding style={{ color: "#000000" }}>
                  {tx.description || "-"}
                </InfoSectionTransactionDetailsPadding>
                <InfoSectionTransactionDetailsPadding>
                  {formatAmountWithColor(tx.amount)}
                </InfoSectionTransactionDetailsPadding>
              </InfoSectionTransactionDetails>
            </InfoSectionTransactionInfoWrapper>
          ))}
        </InfoSectionAccountWrapper>
        <IncomeCalculationSection variables={postScoreVariables} applicantType={applicantType} />
      </InfoSectionLessThanThreeWrapper>
    </InfoSectionWrapper>
  );
};

export default TinkTransactionsView;
